import { dateHandler } from "../utilities/date-utils";
import { createRecurrences, deepClone, getUniqueArrayItems } from "../utilities/general-utils";
import { getAccountsListAndAvailableBalance } from "../utilities/account-utils";
import { svc_getTransactions, svc_getExceptions } from "./transaction.service";
import { svc_getAccounts } from "./account.service";

/**
 * Generate monthly min/max balance data from transactions list
 * @param {Array} transactionsList - Array of transaction objects
 * @returns {Array} Array of monthly min/max data objects
 */
export const getMonthlyMinMaxData = (transactionsList) => {
    const transactions = deepClone(transactionsList);
    const transactionDatesAndBalances = transactions.map((transaction) => {
        const date = dateHandler.toZeroHoursFormat(transaction.instanceDate);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, 0);

        return {
            date: date,
            year: year,
            month: month,
            monthYear: `${month}-${year}`,
            balance: parseFloat(transaction.balance).toFixed(2),
            amount: parseFloat(transaction.amount).toFixed(2),
        };
    });

    const years = getUniqueArrayItems(
        transactionDatesAndBalances.map((transaction) => transaction.year)
    );

    const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

    const getMinMaxByMonth = (year, month) => {
        const currentMonthTransactions = transactionDatesAndBalances.filter(
            (transaction) =>
                transaction.year === year && transaction.month === month
        );

        if (currentMonthTransactions.length === 0) return false;

        const min = currentMonthTransactions.reduce((a, b) =>
            parseFloat(a.balance) < parseFloat(b.balance) ? a : b
        );

        const max = currentMonthTransactions.reduce((a, b) =>
            parseFloat(a.balance) > parseFloat(b.balance) ? a : b
        );

        const date = dateHandler.toZeroHoursFormat(`${year}-${month}-01`);
        const monthName = date.toLocaleString('default', { month: 'long' });
        const displayMonth = `${year} — ${monthName}`;

        return {
            date: date,
            year: year,
            displayMonth: displayMonth,
            minBalance: min.balance,
            maxBalance: max.balance,
        };
    };

    const minMaxByMonth = years.map((year) => {
        const minMaxByMonth = months.map((month) => {
            return getMinMaxByMonth(year, month);
        }).filter((month) => month !== false);

        return minMaxByMonth;
    }).flat();

    return minMaxByMonth;
};

/**
 * Generate net income data from transactions list  
 * @param {Array} transactionsList - Array of transaction objects
 * @returns {Array} Array of monthly net income data objects
 */
export const getNetIncomeData = (transactionsList) => {
    const transactions = deepClone(transactionsList);

    const monthMap = new Map(
        transactions.map((transaction) => {
            const year = transaction.instanceDate.getFullYear();
            const month = transaction.instanceDate.getMonth();

            return [
                `${year}-${month}`,
                {
                    income: 0,
                    expenses: 0,
                    net: 0,
                    date: transaction.instanceDate,
                },
            ];
        })
    );

    transactions.forEach((transaction) => {
        const year = transaction.instanceDate.getFullYear();
        const month = transaction.instanceDate.getMonth();
        const key = `${year}-${month}`;

        if (transaction.amount < 0) {
            monthMap.set(key, {
                ...monthMap.get(key),
                expenses:
                    monthMap.get(key).expenses + parseFloat(transaction.amount),
            });
        } else if (transaction.amount > 0) {
            monthMap.set(key, {
                ...monthMap.get(key),
                income:
                    monthMap.get(key).income + parseFloat(transaction.amount),
            });
        }

        monthMap.set(key, {
            ...monthMap.get(key),
            net: monthMap.get(key).net + parseFloat(transaction.amount),
        });
    });

    const dataAsArray = Array.from(monthMap).map(([key, value]) => {
        const splitKey = key.split('-');

        const month = new Date(splitKey[0], splitKey[1]);
        const monthName = month.toLocaleString('default', { month: 'long' });
        const year = month.getFullYear();
        const displayMonth = `${year} — ${monthName}`;

        return {
            date: value.date,
            year: year,
            month: monthName,
            displayMonth: displayMonth,
            income: parseFloat(value.income).toFixed(2),
            expenses: parseFloat(value.expenses).toFixed(2),
            net: parseFloat(value.net).toFixed(2),
        };
    });

    return dataAsArray;
};

/**
 * Get combined reporting data for dashboard
 * @param {Array} transactionsList - Array of transaction objects  
 * @returns {Object} Object containing minMaxData and netIncomeData
 */
export const getDashboardReportingData = (transactionsList) => {
    const minMaxData = getMonthlyMinMaxData(transactionsList);
    const netIncomeData = getNetIncomeData(transactionsList);

    return {
        minMaxData,
        netIncomeData
    };
};

/**
 * Fetch accounts data for the user
 * @param {string} accessToken - Auth0 access token
 * @param {string} userId - User ID
 * @returns {Array} Array of account objects
 */
const getAccounts = async (accessToken, userId) => {
    const { data } = await svc_getAccounts(accessToken, userId);
    let accounts;

    if (data) {
        accounts = data.map(account => {
            return {
                id: account.id,
                name: account.name,
                type: account.type,
            }
        });
    }

    return accounts;
};

/**
 * Fetch available balance data for the user
 * @param {string} accessToken - Auth0 access token  
 * @param {string} userId - User ID
 * @returns {Object} Available balance data
 */
export const getAvailableBalanceData = async (accessToken, userId) => {
    const { data, error } = await getAccountsListAndAvailableBalance(accessToken, userId);

    if (error) {
        console.error("Error getting available balance: ", error);
        throw error;
    }

    return data.availableBalance;
};

/**
 * Fetch and process transactions data for dashboard reporting
 * @param {string} accessToken - Auth0 access token
 * @param {string} userId - User ID
 * @param {Date} startDate - Date range start
 * @param {Date} endDate - Date range end  
 * @param {Object} availableBalance - Available balance data
 * @returns {Array} Array of processed transaction recurrences
 */
export const getTransactionsData = async (accessToken, userId, startDate, endDate, availableBalance) => {
    const accounts = await getAccounts(accessToken, userId);

    const {
        data: transactions
    } = await svc_getTransactions(
        accessToken,
        userId
    );

    const {
        data: exceptions
    } = await svc_getExceptions(
        accessToken,
        userId
    );

    let recurrences = [];

    if (transactions && transactions.length > 0) {
        if (accounts) {
            transactions.forEach(txn => {
                const account = accounts.find(acc => acc.id === txn.account);

                if (account) {
                    txn.accountName = account.name;
                    txn.accountType = account.type;
                }
            });
        }
        recurrences = createRecurrences(
            transactions,
            exceptions,
            startDate,
            endDate,
            availableBalance,
        );
    }

    return recurrences;
};

/**
 * Get complete dashboard data including transactions and reporting data
 * @param {string} accessToken - Auth0 access token
 * @param {string} userId - User ID
 * @param {Date} startDate - Date range start
 * @param {Date} endDate - Date range end
 * @param {boolean} includeReporting - Whether to include reporting data
 * @returns {Object} Complete dashboard data object
 */
export const svc_getDashboardData = async (accessToken, userId, startDate, endDate, includeReporting = true) => {
    try {
        // Get available balance first
        const availableBalance = await getAvailableBalanceData(accessToken, userId);
        
        if (availableBalance === null) {
            return {
                showOnboardingMessage: true,
                availableBalance: null,
                transactions: null,
                reportingData: null
            };
        }

        // Get transactions data
        const transactions = await getTransactionsData(accessToken, userId, startDate, endDate, availableBalance);

        if (!transactions || transactions.length === 0) {
            return {
                showOnboardingMessage: true,
                availableBalance,
                transactions: null,
                reportingData: null
            };
        }

        // Generate reporting data if requested
        let reportingData = null;
        if (includeReporting) {
            reportingData = getDashboardReportingData(transactions);
        }

        return {
            showOnboardingMessage: false,
            availableBalance,
            transactions,
            reportingData
        };
    } catch (error) {
        console.error("Error fetching dashboard data:", error);
        throw error;
    }
};